import {catchError, filter, first, map, startWith} from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {ResourceService} from '../../../resource/resource.service';
import {EnvironmentConfigService} from '../../../core/services/environment-config.service';
import {SmdTimeline} from './timeline.component';
import {LayoutService} from '../../../layout/layout.service';
import {Endpoint} from '../../../layout/endpoint';
import {
  combineLatest as observableCombineLatest,
  connectable,
  forkJoin,
  Observable,
  of as observableOf,
  ReplaySubject,
} from 'rxjs';
import {Resource} from '../../../resource/resource';
import {CacheService} from '../../../cache/cache.service';
import {AddViewEvent} from '../../../resource/events/addviewevent';
import {MatExpansionPanel} from '@angular/material/expansion';

@Component({
  selector: 'smd-timeline-row',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './timeline-row.component.html',
  styleUrls: ['./timeline-row.component.scss'],
  standalone: false,
})
export class SmdTimelineRowComponent implements OnChanges {
  @Input() rowModel: any;
  @Input() renderCheckbox: boolean;
  requestDetailsAttributes: string[] = [];

  verifiedWorkflowCount$: Observable<number> = observableOf(0);
  verifiedChildRequestCount$: Observable<number> = observableOf(0);

  languageResource: Observable<string>;

  @ViewChild(MatExpansionPanel) public expansionPanel: MatExpansionPanel;

  private currentEndpoint: Endpoint;

  constructor(
    @Inject(forwardRef(() => SmdTimeline)) public _parent: SmdTimeline,
    private dialog: MatDialog,
    public changeDetector: ChangeDetectorRef,
    private resourceService: ResourceService,
    private cacheService: CacheService,
    private layoutService: LayoutService,
    protected envConfig: EnvironmentConfigService,
  ) {
    this.languageResource = this.cacheService.getLanguageResource();

    // Initialize requestDetailsAttributes with environment-specific values
    this.requestDetailsAttributes = [
      this.envConfig.getAttributeName('ObjectID'),
      this.envConfig.getAttributeName('DetailLevel'),
      this.envConfig.getAttributeName('EntryTime'),
    ];

    // Subscribe to endpoint changes
    this.layoutService.endpoint$.subscribe(endpoint => {
      this.currentEndpoint = endpoint;
      this.changeDetector.markForCheck();
    });
  }

  compare(a, b) {
    if (
      a[this.envConfig.getAttributeName('CreatedTime')] >
      b[this.envConfig.getAttributeName('CreatedTime')]
    )
      return -1;
    if (
      a[this.envConfig.getAttributeName('CreatedTime')] <
      b[this.envConfig.getAttributeName('CreatedTime')]
    )
      return 1;
    return 0;
  }

  @HostBinding('class.row-error') rowIsError: boolean = false;
  @HostBinding('class.row-warning') rowIsWarning: boolean = false;
  @HostBinding('class.row-info') rowIsInfo: boolean = false;
  @HostBinding('class.row-default') rowIsDefault: boolean = false;

  ngOnChanges(changes: SimpleChanges): void {
    if ('rowModel' in changes && this.rowModel !== null) {
      this.rowIsError = false;
      this.rowIsWarning = false;
      this.rowIsInfo = false;
      this.rowIsDefault = false;

      switch (this.rowModel[this.envConfig.getAttributeName('RequestStatus')]) {
        case 'Failed':
        case 'Denied':
          this.rowIsError = true;
          break;
        case 'PostProcessingError':
          this.rowIsWarning = true;
          break;
        default:
          if (this.rowModel[this.envConfig.getAttributeName('Operation')] === 'SystemEvent') {
            this.rowIsInfo = true; // icon grau
          } else {
            this.rowIsDefault = true;
          }
      }

      if (this.workflowCounter > 0) {
        const allGuids: string[] = [
          ...(this.rowModel[this.envConfig.getAttributeName('ActionWorkflowInstance')] ?? []),
          ...(this.rowModel[this.envConfig.getAttributeName('AuthenticationWorkflowInstance')] ??
            []),
          ...(this.rowModel[this.envConfig.getAttributeName('AuthorizationWorkflowInstance')] ??
            []),
        ];
        const filter =
          '/' +
          this.envConfig.getObjectTypeName('WorkflowInstance') +
          '[' +
          allGuids
            .map(g => this.envConfig.getAttributeName('ObjectID') + '="' + g + '"')
            .join(' or ') +
          ']';
        this.verifiedWorkflowCount$ = this._parent
          .getCaughtObservable(
            this.resourceService.searchResources(
              filter,
              this.envConfig.getAttributeName('ObjectID'),
              allGuids.length,
            ),
          )
          .pipe(map(fr => fr?.TotalCount ?? 0));
      } else {
        this.verifiedWorkflowCount$ = observableOf(0);
      }

      const hasCollateral = this.rowModel[this.envConfig.getAttributeName('HasCollateralRequest')];
      if (hasCollateral) {
        const parentRequestAttr = this.envConfig.getAttributeName('ParentRequest');
        const objectId = this.rowModel[this.envConfig.getAttributeName('ObjectID')];
        const filter =
          '/' +
          this.envConfig.getObjectTypeName('Request') +
          '[' +
          parentRequestAttr +
          '="' +
          objectId +
          '"]';
        this.verifiedChildRequestCount$ = this._parent
          .getCaughtObservable(
            this.resourceService.searchResources(
              filter,
              this.envConfig.getAttributeName('ObjectID'),
              1,
            ),
          )
          .pipe(map(fr => fr?.TotalCount ?? 0));
      } else {
        this.verifiedChildRequestCount$ = observableOf(0);
      }
    }
  }

  // Getter for template access to environment attributes
  get myEnv() {
    return {
      Attributes: {
        ObjectID: this.envConfig.getAttributeName('ObjectID'),
        DisplayName: this.envConfig.getAttributeName('DisplayName'),
        Creator: this.envConfig.getAttributeName('Creator'),
        Operation: this.envConfig.getAttributeName('Operation'),
        RequestStatus: this.envConfig.getAttributeName('RequestStatus'),
        CreatedTime: this.envConfig.getAttributeName('CreatedTime'),
        DetailMessage: this.envConfig.getAttributeName('DetailMessage'),
        AuthorizationWorkflowInstance: this.envConfig.getAttributeName(
          'AuthorizationWorkflowInstance',
        ),
        AuthenticationWorkflowInstance: this.envConfig.getAttributeName(
          'AuthenticationWorkflowInstance',
        ),
        ActionWorkflowInstance: this.envConfig.getAttributeName('ActionWorkflowInstance'),
        ParentRequest: this.envConfig.getAttributeName('ParentRequest'),
        ApprovalStatus: this.envConfig.getAttributeName('ApprovalStatus'),
        ApprovalStatusMarker: this.envConfig.getAttributeName('ApprovalStatusMarker'),
        ApprovalResponse: this.envConfig.getAttributeName('ApprovalResponse'),
        CommittedTime: this.envConfig.getAttributeName('CommittedTime'),
        Justification: this.envConfig.getAttributeName('Justification'),
        HasCollateralRequest: this.envConfig.getAttributeName('HasCollateralRequest'),
        RequestPhase: this.envConfig.getAttributeName('RequestPhase'),
      },
    };
  }

  get operation(): string {
    if (!this.currentEndpoint?.isCloud()) {
      const displayName = this.rowModel[this.envConfig.getAttributeName('DisplayName')];
      if (displayName.startsWith('Update to')) {
        return 'Update';
      } else if (displayName.startsWith('Create')) {
        return 'Create';
      } else if (displayName.startsWith('Delete')) {
        return 'Delete';
      } else return 'Default';
    } else {
      switch (this.rowModel[this.envConfig.getAttributeName('Operation')]) {
        case 'Modify':
          return 'Update';
        case 'Create':
          return 'Create';
        case 'Delete':
          return 'Delete';
        default:
          return 'Default';
      }
    }
  }

  get iconString(): string {
    const icons = {
      Update: 'edit',
      Create: 'add',
      Delete: 'delete',
      Default: 'help_outline',
    };
    if (this.operation in icons) {
      return icons[this.operation];
    } else return icons.Default;
  }

  private storedModelWithRequestParameter: Observable<Resource> = null;
  private storedModelWithRequestParameterObjectID: string = null;

  get modelWithRequestParameter(): Observable<Resource> {
    if (
      this.storedModelWithRequestParameterObjectID === null ||
      this.storedModelWithRequestParameterObjectID !==
        this.rowModel[this.envConfig.getAttributeName('ObjectID')]
    ) {
      this.storedModelWithRequestParameter = this._parent.getCaughtObservable(
        this.resourceService
          .searchResources(
            '/' +
              this.envConfig.getObjectTypeName('Request') +
              '[' +
              this.envConfig.getAttributeName('ObjectID') +
              '="' +
              this.rowModel[this.envConfig.getAttributeName('ObjectID')] +
              '"]',
            [
              this.envConfig.getAttributeName('DisplayName'),
              this.envConfig.getAttributeName('Target'),
              this.envConfig.getAttributeName('Operation'),
              this.envConfig.getAttributeName('RequestParameter'),
              this.envConfig.getAttributeName('Content'),
              this.envConfig.getAttributeName('Before'),
              this.envConfig.getAttributeName('TargetObjectType'),
            ]
              .filter(x => x !== null)
              .join(','),
            1,
          )
          .pipe(
            map(fr => {
              if (fr.TotalCount !== 1 || fr.Results.length !== 1) {
                return null;
              } else {
                return fr.Results[0];
              }
            }),
          ),
      );
      this.storedModelWithRequestParameterObjectID =
        this.rowModel[this.envConfig.getAttributeName('ObjectID')];
    }
    return this.storedModelWithRequestParameter;
  }

  get targetList(): string[] {
    if (!this.currentEndpoint?.isCloud()) {
      return this._parent.visibleRows?.map(x => x[this.envConfig.getAttributeName('Target')]);
    } else {
      return null;
    }
  }

  getManagementPolicies(grantRight: boolean): Observable<any>[] {
    if (this.rowModel._mgmPolicyCache) {
      return this.rowModel._mgmPolicyCache;
    }

    const mgmPolicyRaw = this.rowModel[this.envConfig.getAttributeName('ManagementPolicy')];
    const mgmPolicies: string[] = Array.isArray(mgmPolicyRaw)
      ? mgmPolicyRaw
      : mgmPolicyRaw != null
        ? [mgmPolicyRaw]
        : [];

    this.rowModel._mgmPolicyCache = mgmPolicies.map((policyGuid: string) => {
      let obs = this._parent.mgmPolicyObservableCache[policyGuid];
      if (obs == null) {
        const filter =
          '/' +
          this.envConfig.getObjectTypeName('Policy') +
          '[' +
          this.envConfig.getAttributeName('ObjectID') +
          '="' +
          policyGuid +
          '"]';
        const attributes = [
          this.envConfig.getAttributeName('DisplayName'),
          this.envConfig.getAttributeName('GrantRight'),
        ].join(',');

        obs = connectable(
          this.resourceService.searchResources(filter, attributes, 1).pipe(
            map(fr => {
              if (fr.TotalCount === 1 && fr.Results.length === 1) {
                return fr.Results[0];
              } else return null;
            }),
          ),
          {connector: () => new ReplaySubject(1), resetOnDisconnect: false},
        );
        this._parent.observableConnections.push(obs.connect());
        this._parent.mgmPolicyObservableCache[policyGuid] = obs;
      }
      return this._parent.getCaughtObservable(
        obs.pipe(
          filter(r => r != null && r[this.envConfig.getAttributeName('GrantRight')] === grantRight),
        ),
      );
    });
    return this.rowModel._mgmPolicyCache;
  }

  getManagementPoliciesCounter(grantRight: boolean): Observable<number> {
    const policies = this.getManagementPolicies(grantRight) ?? [];
    return observableCombineLatest(policies.map(x => x.pipe(startWith(null)))).pipe(
      map(results => {
        return results ? results.filter(r => r !== null).length : 0;
      }),
    );
  }

  get workflowCounter(): number {
    return (
      (this.rowModel[this.envConfig.getAttributeName('ActionWorkflowInstance')]?.length ?? 0) +
      (this.rowModel[this.envConfig.getAttributeName('AuthenticationWorkflowInstance')]?.length ??
        0) +
      (this.rowModel[this.envConfig.getAttributeName('AuthorizationWorkflowInstance')]?.length ?? 0)
    );
  }

  public openDetail(guid: Observable<string> | string | any) {
    if (guid == null) return;
    this.dialog.closeAll();

    if (guid instanceof Observable) {
      guid.pipe(first()).subscribe(g => {
        if (typeof g === 'object') {
          g = g[this.envConfig.getAttributeName('ObjectID')];
        }
        this._parent.onAddView.emit(new AddViewEvent('get', g, null));
        this._parent.onResize();
      });
    } else if (typeof guid === 'string') {
      this._parent.onAddView.emit(new AddViewEvent('get', guid, null));
      this._parent.onResize();
    } else if (typeof guid === 'object') {
      const g = guid[this.envConfig.getAttributeName('ObjectID')];
      if (g != null) {
        this._parent.onAddView.emit(new AddViewEvent('get', g, null));
        this._parent.onResize();
      }
    }
  }

  verifyRequestDetails(request: any, key: string): boolean {
    let details = this.parseRequestDetails(request);
    return (
      details &&
      details[key] &&
      request[this.envConfig.getAttributeName('RequestStatus')] != 'Complete'
    );
  }

  parseRequestDetails(request: any): any {
    let retElem = null;
    let docElem;
    let domParser = new DOMParser();
    let requestStatusDetail: string;

    requestStatusDetail = request.RequestStatusDetail;

    if (requestStatusDetail) {
      retElem = {};
      docElem = domParser.parseFromString(requestStatusDetail, 'application/xml').documentElement;
      retElem[this.envConfig.getAttributeName('ObjectID')] = request.ObjectID;
      retElem[this.envConfig.getAttributeName('DetailMessage')] = docElem.textContent;
      for (let attrName of docElem.getAttributeNames()) {
        if (this.requestDetailsAttributes.includes(attrName)) {
          retElem[attrName] = docElem.getAttribute(attrName);
        }
      }
    }

    return retElem;
  }

  displayNameForResource(guid: string, loadFromBackend = true): Observable<string> {
    return this._parent.displayNameForResource(guid, loadFromBackend);
  }

  getDisplayAttributeName(
    systemName: string,
    objectType: string = this.envConfig.getObjectTypeName('Request'),
  ): Observable<string> {
    return this.cacheService.getDisplayAttribute(systemName, objectType).pipe(
      map(da => ResourceService.convertCamelCaseToNormal(da.DisplayName)),
      catchError(() => observableOf(ResourceService.convertCamelCaseToNormal(systemName))),
    );
  }

  protected getAttributeList(): Observable<string>[] {
    const targetObjectType =
      this.rowModel[this.envConfig.getAttributeName('TargetObjectType') ?? 'TargetObjectType'];

    if (this.envConfig.isCloud()) {
      const contentAttrName = this.envConfig.getAttributeName('Content') ?? 'content';
      return Object.keys(this.rowModel[contentAttrName] ?? {}).map(propertyName =>
        this.cacheService
          .getDisplayAttribute(propertyName, targetObjectType)
          .pipe(map(da => da.DisplayName)),
      );
    } else {
      const requestParamAttrName =
        this.envConfig.getAttributeName('RequestParameter') ?? 'RequestParameter';
      const params = this.rowModel[requestParamAttrName];
      if (!Array.isArray(params)) return [];
      return params.map(paramXml => {
        const matches = /<PropertyName>([^<]*)<\/PropertyName>/.exec(paramXml);
        if (!matches || matches.length !== 2) {
          console.error('PropertyName not found in XML', paramXml);
          return observableOf(null);
        }

        return this.cacheService
          .getDisplayAttribute(matches[1], targetObjectType)
          .pipe(map(da => da.DisplayName));
      });
    }
  }

  protected getAttributeListAsString(): Observable<string> {
    return forkJoin(this.getAttributeList()).pipe(
      map((attributeNames: string[]) => [...new Set(attributeNames)].join(', ')),
    );
  }

  protected openTemplateDlg(templateRef: TemplateRef<any>): void {
    this.dialog.open(templateRef);
  }
}
